import { STANDARD_TIME_VALUE } from "@constants/mgr/KYOTAKUKAIGO/variables";
import { DAY_SELECT_TYPE, FacilityType } from "@constants/variables";
import {
  DetailsInitialValues,
  ServiceDeliveryDetailValues
} from "@initialize/record/serviceDelivery/initialValues";
import { calculateForRequiredTimeNoReverse } from "@utils/domain/mgr/calculateForRequiredTimeNoReverse";
import { calculateHours } from "@utils/domain/mgr/calculateHours";
import { checkTime } from "@validator/rules";
import { checkTimeFutureHHMM } from "@validator/rules/checkTimeFutureHHMM";
import { FormikProps } from "formik";

/**
 * TODO: components/organisms/record/serviceDelivery/ServiceDeliveryTimeContainerなどでも使えるように共通化したい
 * @param fieldName
 * 例）serviceDeliveryRecordPractitioners1
 * @param detailsFieldName
 * 例）serviceDeliveryRecordPractitioners1.serviceDeliveryRecordPractitionerDetails
 * @param setFormikFieldValue
 * (fieldName: string, value: number | string | boolean) => void;
 * @param facilityType
 * 例）IDOSHIEN
 * @param practitionerValues
 * ServiceDeliveryDetailValues["serviceDeliveryRecordPractitioners1"]
 * @param formikProps
 * FormikProps<ServiceDeliveryDetailValues>
 */
export const useServiceDeliveryTimeContainer = (
  fieldName: string,
  detailsFieldName: string,
  setFormikFieldValue: (
    fieldName: string,
    value: number | string | boolean
  ) => void,
  facilityType: FacilityType,
  practitionerValues: ServiceDeliveryDetailValues["serviceDeliveryRecordPractitioners1"],
  formikProps: FormikProps<ServiceDeliveryDetailValues>
): {
  onChangeDateSelect: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onChangeInitialTime: (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    _: string,
    autoCorrectValue: string
  ) => void;
  onChangeActionClass: (index: number, actionClass: number) => void;
  onChangeDetailDateSelect: (
    index: number,
    startDate: string,
    endDate: string,
    isStart: boolean
  ) => void;
  onChangeDetailRecord: (
    index: number,
    start: string,
    end: string,
    isStart: boolean
  ) => void;
  onDeleteRecord: (index: number) => void;
} => {
  // 当日翌日を考慮し、HH:MM形式の時間を00:01~47:59で表示する
  const formatTimeTodayOrNextDay = (time: string, dateNum: string): string => {
    // 00:00の形式でないものはそのまま返す
    if (dateNum === DAY_SELECT_TYPE.today || checkTime(time) || !time) {
      return time;
    }
    const times = time.split(":");
    const hour = Number(times[0]) + 24;
    return `${hour.toString()}:${times[1]}`;
  };

  // 「開始時間・終了時間・運転/空き時間」から時間数計算
  const setPersonalCalculatedHours = (
    inTimeDate: string,
    inTime: string,
    outTimeDate: string,
    outTime: string,
    otherTimeList: DetailsInitialValues,
    isAccompany = false // 自動計算される項目のフィールド名
  ): void => {
    // 自動計算される項目のフィールド名
    const numberOfTimeFieldName = isAccompany
      ? "calculatedAccompanySupportHours"
      : "numberOfTime";
    const inTimeValue = formatTimeTodayOrNextDay(inTime, inTimeDate);
    const outTimeValue = formatTimeTodayOrNextDay(outTime, outTimeDate);
    const baseTime = calculateForRequiredTimeNoReverse(
      inTimeValue,
      outTimeValue
    );
    if (baseTime) {
      const otherTime = otherTimeList.reduce((sum, value) => {
        if (value.isDelete) {
          return sum;
        }
        // 開始時間または終了時間が存在しない場合は計算しない
        if (value.startTime.length === 0 || value.endTime.length === 0) {
          return sum;
        }
        const inTimeDetails = formatTimeTodayOrNextDay(
          value.startTime,
          value.startTimeDate
        );
        // 基準となる時間未満の場合は基準の時間に設定する
        const inTimeDetailsValues = checkTimeFutureHHMM(
          inTimeDetails,
          inTimeValue
        )
          ? inTimeValue
          : inTimeDetails;

        const outTimeDetails = formatTimeTodayOrNextDay(
          value.endTime,
          value.endTimeDate
        );
        // 基準となる時間を超える場合は基準の時間に設定する
        const outTimeDetailsValues = checkTimeFutureHHMM(
          outTimeDetails,
          outTimeValue
        )
          ? outTimeDetails
          : outTimeValue;
        const time = calculateForRequiredTimeNoReverse(
          inTimeDetailsValues,
          outTimeDetailsValues
        );
        return time ? sum + time : sum;
      }, 0);
      const result = calculateHours(
        baseTime,
        STANDARD_TIME_VALUE.HALF,
        otherTime,
        STANDARD_TIME_VALUE.HALF
      );
      const resultText =
        result > 0 && !String(result).includes(".")
          ? `${result}.0`
          : String(result);

      setFormikFieldValue(
        `${fieldName}.${numberOfTimeFieldName}`,
        !result || Number.isNaN(result) ? "" : resultText
      );
    } else {
      setFormikFieldValue(`${fieldName}.${numberOfTimeFieldName}`, "");
    }
  };

  // 「当日・翌日」セレクトボックス変更時
  const onChangeDateSelect = (
    event: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const start = practitionerValues.startTime;
    const end = practitionerValues.endTime;
    const startTimeFieldName = "startTime";
    const endTimeFieldName = "endTime";
    const otherTime =
      practitionerValues.serviceDeliveryRecordPractitionerDetails;
    formikProps.setFieldTouched(`${fieldName}.${startTimeFieldName}`, true);
    formikProps.setFieldTouched(`${fieldName}.${endTimeFieldName}`, true);
    if (event.target.name === `${fieldName}.${startTimeFieldName}Date`) {
      formikProps.setFieldTouched(`${fieldName}.${endTimeFieldName}Date`, true);
      setPersonalCalculatedHours(
        event.target.value,
        start,
        practitionerValues.endTimeDate,
        end,
        otherTime,
        false
      );
    } else {
      formikProps.setFieldTouched(`${fieldName}.startTimeDate`, true);
      setPersonalCalculatedHours(
        practitionerValues.startTimeDate,
        start,
        event.target.value,
        end,
        otherTime,
        false
      );
    }
  };

  // 「開始時間・終了時間」時間変更時
  const onChangeInitialTime = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    _: string,
    autoCorrectValue: string
  ): void => {
    // 「同行支援時間」の場合はformikのフィールド名変更
    const startDate = practitionerValues.startTimeDate;
    const endDate = practitionerValues.endTimeDate;
    const startTimeFieldName = "startTime";
    const endTimeFieldName = "endTime";

    const otherTime =
      practitionerValues.serviceDeliveryRecordPractitionerDetails;
    formikProps.setFieldTouched(`${fieldName}.${startTimeFieldName}Date`, true);
    formikProps.setFieldTouched(`${fieldName}.${endTimeFieldName}Date`, true);
    if (event.target.name === `${fieldName}.${startTimeFieldName}`) {
      formikProps.setFieldTouched(`${fieldName}.${endTimeFieldName}`, true);
      const end = practitionerValues.endTime;
      setPersonalCalculatedHours(
        startDate,
        autoCorrectValue,
        endDate,
        end,
        otherTime,
        false
      );
    } else {
      formikProps.setFieldTouched(`${fieldName}.startTime`, true);
      const start = practitionerValues.startTime;

      setPersonalCalculatedHours(
        startDate,
        start,
        endDate,
        autoCorrectValue,
        otherTime,
        false
      );
    }
  };

  // TODO: UnitsFieldsコンポーネントで必須となっているので移動支援で使わないですが定義しておきます
  // 「運転・空き」「当日・翌日」セレクトボックス変更時(重度のみ)
  const onChangeActionClass = (index: number, actionClass: number): void => {
    if (facilityType === FacilityType.JUDOHOMONKAIGO) {
      const otherTime = practitionerValues.serviceDeliveryRecordPractitionerDetails.map(
        (item, i) => {
          if (i === index) {
            return {
              serviceDeliveryRecordPractitionerDetailsId:
                item.serviceDeliveryRecordPractitionerDetailsId,
              actionClass,
              startTimeDate: item.startTimeDate,
              startTime: item.startTime,
              endTimeDate: item.endTimeDate,
              endTime: item.endTime,
              isDelete: item.isDelete
            };
          }
          return item;
        }
      );
      setPersonalCalculatedHours(
        practitionerValues.startTimeDate,
        practitionerValues.startTime,
        practitionerValues.endTimeDate,
        practitionerValues.endTime,
        otherTime
      );
    }
  };

  // 「運転・空き」「当日・翌日」セレクトボックス変更時
  const onChangeDetailDateSelect = (
    index: number,
    startDate: string,
    endDate: string,
    isStart: boolean
  ): void => {
    const otherTime = practitionerValues.serviceDeliveryRecordPractitionerDetails.map(
      (item, i) => {
        if (i === index) {
          formikProps.setFieldTouched(
            `${detailsFieldName}[${index}].startTime`,
            true
          );
          formikProps.setFieldTouched(
            `${detailsFieldName}[${index}].endTime`,
            true
          );
          if (isStart) {
            formikProps.setFieldTouched(
              `${detailsFieldName}[${index}].endTimeDate`,
              true
            );
          } else {
            formikProps.setFieldTouched(
              `${detailsFieldName}[${index}].startTimeDate`,
              true
            );
          }
          return {
            serviceDeliveryRecordPractitionerDetailsId:
              item.serviceDeliveryRecordPractitionerDetailsId,
            actionClass: item.actionClass,
            startTimeDate: startDate,
            startTime: item.startTime,
            endTimeDate: endDate,
            endTime: item.endTime,
            isDelete: item.isDelete
          };
        }
        return item;
      }
    );
    setPersonalCalculatedHours(
      practitionerValues.startTimeDate,
      practitionerValues.startTime,
      practitionerValues.endTimeDate,
      practitionerValues.endTime,
      otherTime
    );
  };

  // 「運転・空き」時間変更時
  const onChangeDetailRecord = (
    index: number,
    start: string,
    end: string,
    isStart: boolean
  ): void => {
    const otherTime = practitionerValues.serviceDeliveryRecordPractitionerDetails.map(
      (item, i) => {
        if (i === index) {
          formikProps.setFieldTouched(
            `${detailsFieldName}[${index}].startTimeDate`,
            true
          );
          formikProps.setFieldTouched(
            `${detailsFieldName}[${index}].endTimeDate`,
            true
          );
          if (isStart) {
            formikProps.setFieldTouched(
              `${detailsFieldName}[${index}].endTime`,
              true
            );
          } else {
            formikProps.setFieldTouched(
              `${detailsFieldName}[${index}].startTime`,
              true
            );
          }
          return {
            serviceDeliveryRecordPractitionerDetailsId:
              item.serviceDeliveryRecordPractitionerDetailsId,
            actionClass: item.actionClass,
            startTimeDate: item.startTimeDate,
            startTime: start,
            endTimeDate: item.endTimeDate,
            endTime: end,
            isDelete: item.isDelete
          };
        }
        return item;
      }
    );
    setPersonalCalculatedHours(
      practitionerValues.startTimeDate,
      practitionerValues.startTime,
      practitionerValues.endTimeDate,
      practitionerValues.endTime,
      otherTime
    );
  };

  // 「運転/空き時間」削除時
  const onDeleteRecord = (index: number): void => {
    const removeDetailsValues = [
      ...practitionerValues.serviceDeliveryRecordPractitionerDetails
    ];
    removeDetailsValues.splice(index, 1);
    setPersonalCalculatedHours(
      practitionerValues.startTimeDate,
      practitionerValues.startTime,
      practitionerValues.endTimeDate,
      practitionerValues.endTime,
      removeDetailsValues
    );
  };

  return {
    onChangeDateSelect,
    onChangeInitialTime,
    onChangeActionClass,
    onChangeDetailDateSelect,
    onChangeDetailRecord,
    onDeleteRecord
  };
};
